import React, { FC, useEffect, useState } from "react";
import { ActivityIndicator, DeviceEventEmitter, KeyboardAvoidingView, Platform, RefreshControl, TouchableOpacity, useWindowDimensions, View, VirtualizedList } from "react-native";
import { ThemeConsumer } from "@rneui/themed";
import { shallowEqual } from "react-redux";

import { ZenBadge, ZenDivider, ZenEmptyComponent, ZenFilterModal, ZenIcon, ZenLoader, ZenText, ZenToast } from "zen-core-elements";
import { filterItemsProps } from "zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal";

import { config } from "../../../shared/environment";
import CommonHeader from "../../../shared/commonComponents/commonHeader";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useAppNavigation, useAppRoute } from "../../../types/@react-navigation/navigation";
import { activityLogFilterType, ActivityLogList } from "./activityLogModule";
import { getActivityLogsApi, getDeviceHistoryApi } from "../../../redux/slices/activityLogSlice";
import { CustomModal } from "../../../shared/commonComponents/customModal";
import useBackHandler from "../../../shared/hooks/useBackHandler";
import moment from "moment";
import { showApiError } from '../../../shared/utils/handleApiError';

const ActivityLogs: FC = () => {
    const navigation = useAppNavigation()
    const { width } = useWindowDimensions()
    const dispatch = useAppDispatch()
    const { value: currentUser } = useAppSelector((state) => state?.common?.currentUser, shallowEqual)
    const { Listcount, activityLog, deviceHistory } = useAppSelector((state) => state?.activityLog)
    useBackHandler()
    const staffId = useAppRoute("StaffLoginHistory")?.params?.staffId
    const [offset, setOffset] = useState(0)
    const [loader, setLoader] = useState(true)
    const [refreshLoader, setRefreshLoader] = useState(false)
    const [endReachLoader, setEndReachLoader] = useState(false)
    //variable to store selected filter
    const [isVisibleFilter, setIsVisibleFilter] = useState(false)
    //variable to store filter count
    const [filtercount, setFilterCount] = useState(0)
    const [transparentLoader, setTransparentLoader] = useState(false)
    const [filterValue, setFilterValue] = useState<string | null>("")

    //variable to store filter history data
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: '1',
            filterName: 'Custom Date',
            filterType: "dateRange",
            initialSelected: { startDate: "", endDate: "" },
            filterValue: { minDate: "", maxDate: "" }
        },
        {
            id: "2",
            filterName: "Device Name",
            filterType: "dropdown",
            filterValue: [],
            initialSelected: "",
        },
        {
            id: '3',
            filterName: "Medium",
            filterType: "radio",
            initialSelected: "",
            filterValue: []
        }
    ])

    useEffect(() => {
        if (filterValue !== "") {
            toggleOffsetValue()
        }
    }, [filterValue])

    useEffect(() => {
        getActivityLogs()
    }, [offset])

    const toggleOffsetValue = () => {
        setOffset((prev) => prev !== 0 ? 0 : -1)
    }

    const getActivityLogs = async () => {
        await dispatch(getActivityLogsApi({
            storeId: config?.storeId,
            userId: staffId || currentUser?.id!,
            payload: {
                filterData: filterValue || null,
                limit: width >= 900 ? 20 : 10,
                offset: offset == -1 ? 0 : offset
            }
        })).unwrap().catch(showApiError).finally(() => {
            setLoader(false);
            setEndReachLoader(false);
            setRefreshLoader(false)
        })
    }

    useEffect(() => {
        if (deviceHistory) {
            setFilterData((prev) => {
                let data = [...prev]
                if (data[1].filterType == "dropdown") {
                    data[1].filterValue = deviceHistory?.deviceName?.map((item) => ({
                        label: item?.device_name,
                        value: item?.device_name
                    }))
                }
                if (data[2].filterType == "radio") {
                    data[2].filterValue = deviceHistory?.browserName?.map((item) => ({
                        label: item?.browser,
                        value: item?.browser
                    }))
                }
                return data
            })
        }
    }, [deviceHistory])

    const getDeviceHistory = async () => {
        setTransparentLoader(true)
        await dispatch(getDeviceHistoryApi({ storeId: config?.storeId, userId: staffId || currentUser?.id! })).unwrap().then(() => {
            setIsVisibleFilter(true)
        }).catch(() => {
            ZenToast({
                type: "error",
                message: "Failed to get the device history"
            })
        }).finally(() => {
            setTransparentLoader(false)
        })
    }

    const openFilterModal = () => {
        if (!deviceHistory) {
            getDeviceHistory()
            return
        }
        setIsVisibleFilter(true)
    }

    const renderItem = ({ item, index }: { item: ActivityLogList[], index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <View key={index} style={{ flexDirection: "row" }}>
                        {item?.map((log: ActivityLogList, ResponseDataIndex: number) => (
                            <View key={ResponseDataIndex} style={{ flex: 1 / 1 }}>
                                <View style={{
                                    backgroundColor: theme?.light, marginBottom: 10,
                                    marginHorizontal: 10,
                                    padding: 10, borderRadius: 5, gap: 10
                                }}>
                                    <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                                        <View style={{ gap: 5 }}>
                                            <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                                                <ZenText content={"IP Address:"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} />
                                                <ZenText content={log?.ipAddress || "N/A"} size={theme?.smallFontSize} style={{ fontFamily: theme?.bold, color: theme?.lightDark }} />
                                            </View>
                                            <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                                                <ZenText content={"Medium:"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} />
                                                <ZenText content={log?.appDeviceDetail.browser} size={theme?.smallFontSize} style={{ fontFamily: theme?.bold, color: theme?.lightDark }} />
                                            </View>
                                        </View>
                                        <View style={{ gap: 5, alignItems: "flex-end" }}>
                                            <ZenText content={"Device Name"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} />
                                            <ZenText content={log?.appDeviceDetail.deviceName} size={theme?.smallFontSize} style={{ fontFamily: theme?.bold, color: theme?.lightDark }} />
                                        </View>
                                    </View>
                                    <ZenDivider style={{ height: 0.6, backgroundColor: theme?.lightGrey }} />
                                    <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                                        <View style={{ gap: 5 }}>
                                            <ZenText content={"Login Time"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} />
                                            <ZenText content={log?.loginTime ? moment(log?.loginTime)?.format("DD/MM/YYYY HH:mm:ss A") : "N/A"} size={theme?.smallFontSize} style={{ fontFamily: theme?.bold, color: theme?.lightDark }} />
                                        </View>
                                        <View style={{ gap: 5, alignItems: "flex-end" }}>
                                            <ZenText content={"Logout Time"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} />
                                            <ZenText content={log?.loggedOutTime ? moment(log?.loggedOutTime)?.format("DD/MM/YYYY HH:mm:ss A") : "N/A"} size={theme?.smallFontSize} style={{ fontFamily: theme?.bold, color: theme?.lightDark }} />
                                        </View>
                                    </View>
                                </View>
                            </View>
                        ))}
                    </View>
                )}
            </ThemeConsumer>
        )
    }

    const getItem = (item: any[], index: number) => {
        let array: any = [];
        let numOfColumns = 1
        for (let i = 0; i < numOfColumns; i++) {
            let temp = item?.[index * numOfColumns + i];
            temp && array?.push(temp);
        }
        return array;
    };

    const footerComponent = () => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    endReachLoader ?
                        <View>
                            <ActivityIndicator size={"small"} color={theme?.primary} />
                        </View >
                        : undefined
                )}
            </ThemeConsumer>
        )
    }

    const onRefresh = () => {
        DeviceEventEmitter?.emit("clearFilter")
        setFilterValue("")
        setRefreshLoader(true)
        toggleOffsetValue()
    }
    const onEndReached = () => {
        setEndReachLoader(true)
        let limit = width >= 900 ? 20 : 10
        let offsetValue = offset == -1 ? 0 : offset
        let newOffset = offsetValue + limit
        if (Listcount < newOffset) {
            newOffset = newOffset - (newOffset - Listcount)
        }
        setOffset(newOffset)
    }
    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} enabled style={[theme?.sellScreenStyle?.outerContainer, { gap: 5 }]}>
                    <CommonHeader headerName={staffId ? "Staff Login History" : "Activity Logs"} backIcon backIconOnpress={() => navigation?.goBack()} />
                    <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: 10, paddingHorizontal: 15 }}>
                        <View>
                            <ZenText content="Login History" size={theme.smallerFontSize} bold color={theme.lightDark} />
                            <ZenText content={staffId ? "View login history here" : "View activity logs here"} size={theme.smallHeaderFontSize} />
                        </View>
                        <TouchableOpacity onPress={() => { openFilterModal() }}>
                            {filtercount ?
                                <ZenBadge
                                    badgeName={filtercount.toString()}
                                    badgeColor={theme?.primary}
                                    badgeTextColor={theme.light}
                                    badgeTextSize={"small"}
                                    badgeContainerStyle={{ position: "absolute", right: -7, top: -7, paddingHorizontal: 7 }}
                                />
                                : null}
                            <ZenIcon name="filter-variant" type="material-community" size={theme?.iconSize} color={theme.grey} style={{ borderRadius: 5, borderColor: theme.grey, borderWidth: 0.5, padding: 5 }} />
                        </TouchableOpacity>
                    </View>
                    {(loader && !refreshLoader && !endReachLoader) ?
                        <ZenLoader lottie={require('../../../assets/infinityLoader.json')} lottieType='Local' viewStyle={{ backgroundColor: "transparent" }} /> :
                        <View style={{ flex: 1 }}>
                            <VirtualizedList
                                keyExtractor={(_: any, index: number) => index?.toString()}
                                getItemCount={(data: any) => data?.length}
                                getItem={getItem}
                                scrollEnabled={true}
                                nestedScrollEnabled={true}
                                renderItem={renderItem}
                                data={activityLog || []}
                                keyboardDismissMode="on-drag"
                                showsVerticalScrollIndicator={false}
                                onEndReachedThreshold={0.01}
                                contentContainerStyle={[{ paddingBottom: activityLog.length ? 10 : undefined, flex: activityLog?.length ? undefined : 1 }]}
                                ListFooterComponent={footerComponent}
                                ListFooterComponentStyle={{ height: activityLog.length ? 70 : 0 }}
                                ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: theme?.light }} />}
                                onEndReached={onEndReached}
                                refreshControl={<RefreshControl refreshing={refreshLoader} onRefresh={onRefresh} />}
                            />
                        </View>
                    }
                    <ZenFilterModal
                        setVisible={setIsVisibleFilter}
                        visible={isVisibleFilter}
                        filterItems={filterData}
                        setFilterItems={setFilterData}
                        filterCount={filtercount}
                        setFilterCount={setFilterCount}
                        title="Activity Logs Filter"
                        isSingleExpand={false}
                        onConfirm={(value) => {
                            setLoader(true)
                            const filterResult: activityLogFilterType = {
                                ...(value?.values?.["Device Name"] && { device_name: value.values["Device Name"] }),
                                ...(value?.values?.Medium && { browser: value.values.Medium }),
                                ...(value?.values?.["Custom Date"]?.startDate && {
                                    customDateRange: {
                                        begin: value.values["Custom Date"].startDate,
                                        end: value.values["Custom Date"].endDate,
                                    },
                                })
                            }
                            setFilterValue(Object.keys(filterResult).length > 0 ? JSON.stringify(filterResult) : null)
                        }
                        }
                    />
                    <CustomModal transparent visible={transparentLoader}>
                        <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                    </CustomModal>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    )

}

export default ActivityLogs